/**
 * Délai de paiement d'une facture.
 *
 * Un client qui a négocié soixante jours les a pour toutes ses factures.
 * Retaper le délai à chaque pièce finit par produire l'erreur qui compte : on
 * relance un client qui n'est pas en retard, ou l'on laisse dormir une créance
 * qui l'est.
 */

/** Délai retenu à défaut de tout autre. */
export const DELAI_PAR_DEFAUT = 30;

/** Délai le plus long que le serveur accepte, en jours. */
export const DELAI_MAXIMAL = 365;

/**
 * Délai saisi à l'écran, ramené à ce que le serveur accepte.
 *
 * <p>Le champ laissait écrire 400 ou −5 : le serveur refusait la fiche entière
 * (400), et la modification du client était perdue sans que l'écran dise
 * pourquoi. La saisie est bornée là où elle se fait.
 *
 * @param {string|number} saisie valeur du champ
 * @returns {number|''} un nombre entier de jours entre 0 et le maximum, ou
 *          vide pour « délai habituel »
 */
export function delaiSaisi(saisie) {
  if (saisie === '' || saisie == null) return '';
  const jours = Math.round(Number(saisie));
  if (!Number.isFinite(jours)) return '';
  return Math.min(DELAI_MAXIMAL, Math.max(0, jours));
}

/** Libellé d'un délai nul : la facture est payable dès qu'elle est reçue. */
export const A_RECEPTION = 'À réception';

/**
 * Délais proposés dans les listes fermées, comme la maquette : 15, 30, 45 et
 * 60 jours, ou à réception (0 jour).
 */
export const DELAIS_PROPOSES = [15, 30, 45, 60, 0];

/**
 * Libellé d'un délai, tel que les écrans et les documents l'écrivent.
 *
 * @param {number} jours délai en jours
 * @returns {string} « 30 jours », ou « À réception » pour un délai nul
 */
export function libelleDelai(jours) {
  return Number(jours) === 0 ? A_RECEPTION : `${jours} jours`;
}

/**
 * Nombre de jours d'un délai enregistré : `''`, `null` ou une saisie
 * illisible ne disent rien, et 0 veut dire « à réception ».
 *
 * @param {*} valeur délai enregistré
 * @returns {number|null} les jours, ou null s'il n'y a pas de délai
 */
function joursOuRien(valeur) {
  if (valeur === '' || valeur == null) return null;
  const jours = Number(valeur);
  return Number.isFinite(jours) && jours >= 0 ? jours : null;
}

/**
 * Délai de paiement par défaut de l'entreprise, réglé dans Réglages →
 * Documents. Un ancien réglage rangé tel quel dans les paramètres reste lu.
 *
 * @param {object} [soc] entreprise émettrice
 * @returns {number} le délai, en jours
 */
export function delaiMaison(soc) {
  return (
    joursOuRien(soc?.delaiPaiementDefaut) ??
    joursOuRien(soc?.parametres?.delaiPaiementDefaut) ??
    DELAI_PAR_DEFAUT
  );
}

/**
 * Délai de paiement applicable à un client.
 *
 * L'accord particulier passe avant l'usage de la maison, et l'usage de la
 * maison avant la valeur de repli. Un délai de 0 jour est un accord à part
 * entière (« à réception ») : il ne renvoie pas au délai de la maison.
 *
 * @param {object} [client] client concerné
 * @param {object} [soc] entreprise émettrice
 * @returns {number} le délai, en jours
 */
export function delaiPour(client, soc) {
  return joursOuRien(client?.delaiPaiement) ?? delaiMaison(soc);
}

/**
 * Échéance à inscrire sur la facture d'un client.
 *
 * @param {object} [client] client concerné
 * @param {object} [soc] entreprise émettrice
 * @returns {string} l'échéance, telle que les écrans l'écrivent
 */
export function echeancePour(client, soc) {
  return libelleDelai(delaiPour(client, soc));
}

/**
 * Acompte d'une offre neuve tant que Réglages → Documents n'en fixe pas :
 * aucun. La case reste vide ; c'est le réglage de l'entreprise qui la remplit,
 * jamais un chiffre choisi à sa place.
 */
export const ACOMPTE_PAR_DEFAUT = 0;

/**
 * Acompte, en pour-cent, proposé sur une nouvelle offre. Il ne vaut que pour
 * les offres à créer : une offre existante garde l'acompte qu'elle porte.
 *
 * @param {object} [soc] entreprise émettrice
 * @returns {number} le pourcentage
 */
export function acompteParDefaut(soc) {
  const pct = soc?.acompteDefaut;
  if (pct === '' || pct == null) return ACOMPTE_PAR_DEFAUT;
  const n = Number(pct);
  return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : ACOMPTE_PAR_DEFAUT;
}

/**
 * Remise globale, en pour-cent, proposée sur une nouvelle offre (Réglages →
 * Documents) : aucune tant que l'entreprise n'en a pas réglé.
 *
 * @param {object} [soc] entreprise émettrice
 * @returns {number} le pourcentage
 */
export function remiseParDefaut(soc) {
  const n = Number(String(soc?.remiseDefaut ?? '').replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? Math.min(100, n) : 0;
}

/** Durées de validité proposées pour les offres, en jours. */
export const VALIDITES_PROPOSEES = [15, 30, 45, 60, 90];

/** Validité d'une offre neuve tant que Réglages → Documents n'en fixe pas. */
export const VALIDITE_PAR_DEFAUT = 30;

/**
 * Combien de jours une nouvelle offre reste valable (Réglages → Documents) :
 * la date « Valable jusqu'au » du PDF en découle. Une offre existante garde
 * la sienne.
 *
 * @param {object} [soc] entreprise émettrice
 * @returns {number} le nombre de jours, 1 au moins
 */
export function validiteParDefaut(soc) {
  const n = Math.round(Number(String(soc?.validiteOffreDefaut ?? '').replace(',', '.')));
  return Number.isFinite(n) && n > 0 ? Math.min(n, 3650) : VALIDITE_PAR_DEFAUT;
}
